import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { esTardio } from 'lib/tardio';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet, Filter, ChevronRight, ClipboardList, AlertOctagon, AlertTriangle, X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { fetchInspeccionesPeriodo, fetchDiasSinOperacion } from 'services/adminService';
import {
  filtrarInspecciones, vigentes, fechaHoraCR, coberturaPorDia, resumenCobertura, fechaCorta, plural,
  metodoIdentificacion, METODO_LABEL, nombreUsuario, idsUsuarios, AVISO_SIN_OPERACION,
} from 'lib/auditoria';
import { checklistDe, etiquetasDe, tituloInstalacion, etiquetaInstalacion } from 'lib/auditoriaInstalaciones';
import { hoyCR } from 'lib/fechaCR';
import { ESTADO_FILA } from 'lib/estadoUi';
import { neutralizarFormula } from 'lib/auditoriaCsv';
import { rutaInspeccion } from 'lib/auditoriaInspeccion';
import { useAuth } from 'contexts/AuthContext';
import AppShell from 'components/AppShell';
import SelectorInstalacion, { useInstalacion } from 'components/SelectorInstalacion';
import Kpi from 'components/Kpi';
import Cargando from 'components/Cargando';
import useNombres from 'components/useNombres';
import EstadoPill, { MarcaTardio, MarcaAnulada } from 'components/EstadoPill';
import PeriodoSelector, { periodoInicial, ATAJOS_HISTORIAL } from 'components/PeriodoSelector';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';
import Table from 'components/ui/Table';
import ErrorState from 'components/ui/ErrorState';
import EmptyState from 'components/ui/EmptyState';
import { mensajeError } from 'lib/errores';

const POR_PAGINA = 50;
const cntF = (r, v) => Object.values(r?.resultados || {}).filter((x) => x === v).length;

function Marcas({ r }) {
  if (!esTardio(r) && !r.anulada) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {esTardio(r) && <MarcaTardio />}
      {r.anulada && <MarcaAnulada />}
    </span>
  );
}

function Conteos({ r }) {
  const f = cntF(r, 'falla');
  const a = cntF(r, 'alerta');
  if (!f && !a) return null;
  return (
    <div className="mt-1.5 flex gap-3 text-xs font-medium">
      {f > 0 && <span className="tabular-nums text-error">{plural(f, 'falla', 'fallas')}</span>}
      {a > 0 && <span className="tabular-nums text-warning-foreground">{plural(a, 'alerta', 'alertas')}</span>}
    </div>
  );
}

// Frase bajo los indicadores: anuladas y días sin inspección, o por qué no se
// pudieron calcular (N3: antes la frase desaparecía sin avisar).
export function fraseIndicadores({ hayFiltros, anuladas, sinOpPeriodo, diasSinRegistro }) {
  const partes = [];
  if (hayFiltros) partes.push('Con los filtros aplicados.');
  if (anuladas > 0) partes.push(`${plural(anuladas, 'anulada', 'anuladas')}, no ${anuladas === 1 ? 'cuenta' : 'cuentan'}.`);
  if (!hayFiltros) {
    if (sinOpPeriodo === null) partes.push(AVISO_SIN_OPERACION);
    else partes.push(`${plural(diasSinRegistro, 'día sin inspección', 'días sin inspección')}.`);
  }
  return partes.join(' ');
}

export default function Inspecciones() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const hoy = hoyCR();
  const [instalacion, setInstalacion] = useInstalacion();

  const [periodo, setPeriodo] = useState(() => periodoInicial('30'));
  const [rows, setRows] = useState([]);
  const [sinOpPeriodo, setSinOpPeriodo] = useState(null); // null = no se pudo consultar
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({ guia: '', estado: '' });
  const [visibles, setVisibles] = useState(POR_PAGINA);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [r, s] = await Promise.all([
        fetchInspeccionesPeriodo({ ...periodo, instalacion }),
        fetchDiasSinOperacion({ ...periodo, instalacion }).catch(() => null),
      ]);
      setRows(r);
      setSinOpPeriodo(s);
      setVisibles(POR_PAGINA);
    } catch (e) {
      setError(e);
    } finally {
      setCargando(false);
    }
  }, [periodo, instalacion]);

  useEffect(() => { cargar(); }, [cargar]);

  const nombres = useNombres(useMemo(() => idsUsuarios(rows), [rows]));
  const filtradas = useMemo(() => filtrarInspecciones(rows, filtros), [rows, filtros]);
  const hayFiltros = !!(filtros.guia || filtros.estado);
  const kpis = useMemo(() => {
    const v = vigentes(filtradas);
    return {
      total: v.length,
      falla: v.filter((r) => r.estado_general === 'falla').length,
      alerta: v.filter((r) => r.estado_general === 'alerta').length,
      anuladas: filtradas.length - v.length,
    };
  }, [filtradas]);
  const diasSinRegistro = useMemo(() => {
    if (!sinOpPeriodo) return null;
    return resumenCobertura(coberturaPorDia({
      inspecciones: rows, diasSinOperacion: sinOpPeriodo, desde: periodo.desde, hasta: periodo.hasta, hoy,
    })).sinRegistro;
  }, [rows, sinOpPeriodo, periodo, hoy]);

  const exportar = () => {
    const items = checklistDe(instalacion)?.items || [];
    const label = etiquetasDe(instalacion);
    const resumen = [
      ['Vista Los Sueños', tituloInstalacion(instalacion)],
      ['Instalación', etiquetaInstalacion(instalacion)],
      ['Período', `${periodo.desde} a ${periodo.hasta}`],
      ['Filtro guía', filtros.guia || 'Todos'],
      ['Filtro estado', filtros.estado || 'Todos'],
      ['Generado (CR)', fechaHoraCR(new Date().toISOString())],
      ['Generado por', user?.email || ''],
      ['Inspecciones vigentes', kpis.total],
      ['Con falla', kpis.falla],
      ['Con alerta', kpis.alerta],
      ['Anuladas (no cuentan)', kpis.anuladas],
    ];
    const data = filtradas.map((r) => ({
      ID: r.id,
      Fecha: r.fecha,
      'Hora de inicio (CR)': fechaHoraCR(r.iniciada_at),
      'Hora dispositivo (CR)': fechaHoraCR(r.fecha_hora),
      'Hora servidor (CR)': fechaHoraCR(r.created_at),
      Guía: r.guia_nombre,
      Identificación: METODO_LABEL[metodoIdentificacion(r, nombres)],
      'Colaborador ID': r.colaborador_id || '',
      'Registrada por': r.created_by ? nombreUsuario(r.created_by, nombres) : '',
      Estado: r.estado_general,
      Tardío: r.registro_tardio ? 'sí' : 'no',
      Anulada: r.anulada ? 'sí' : 'no',
      'Motivo de anulación': r.anulada_motivo || '',
      'Anulada por': r.anulada_por ? nombreUsuario(r.anulada_por, nombres) : '',
      Fallas: cntF(r, 'falla'),
      Alertas: cntF(r, 'alerta'),
      ...Object.fromEntries(items.map((it) => [it.label, r.resultados?.[it.key] || ''])),
      Notas: Object.entries(r.detalles || {}).filter(([, v]) => v?.nota)
        .map(([k, v]) => `${label[k] || k}: ${v.nota}`).join(' | '),
      Comentarios: r.comentarios || '',
      Origen: r.origen || '',
    }));
    // Texto libre (guía, notas, comentarios, motivos) sin fórmulas ejecutables.
    const seguro = data.map((o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, neutralizarFormula(v)])));
    const resumenSeguro = resumen.map((f) => f.map(neutralizarFormula));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumenSeguro), 'Resumen');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(seguro), 'Inspecciones');
    XLSX.writeFile(wb, `cables-inspecciones-${instalacion.replace(/_/g, '-')}-${periodo.desde}_a_${periodo.hasta}.xlsx`);
  };

  const lista = filtradas.slice(0, visibles);
  const cambiarFiltro = (k, v) => { setFiltros((f) => ({ ...f, [k]: v })); setVisibles(POR_PAGINA); };

  return (
    <AppShell titulo="Inspecciones" descripcion={tituloInstalacion(instalacion)}
      acciones={(
        <Button variant="outline" onClick={exportar} disabled={cargando || !!error}>
          <FileSpreadsheet size={16} className="mr-1.5" aria-hidden="true" />Exportar Excel
        </Button>
      )}>
      <div className="space-y-4">
        <SelectorInstalacion value={instalacion} onChange={setInstalacion} />
        <PeriodoSelector value={periodo} onChange={setPeriodo} atajos={ATAJOS_HISTORIAL} />

        <div>
          <div className="grid grid-cols-3 gap-2.5">
            <Kpi icon={ClipboardList} label="Inspecciones" value={cargando ? '…' : kpis.total} />
            <Kpi icon={AlertOctagon} label="Con falla" value={cargando ? '…' : kpis.falla} tono="falla" />
            <Kpi icon={AlertTriangle} label="Con alerta" value={cargando ? '…' : kpis.alerta} tono="alerta" />
          </div>
          {!cargando && !error && (
            <p className="mt-1.5 text-xs text-muted-foreground" data-testid="kpi-periodo">
              {fraseIndicadores({ hayFiltros, anuladas: kpis.anuladas, sinOpPeriodo, diasSinRegistro })}
            </p>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-3">
          <div className="mb-2.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            <Filter size={13} aria-hidden="true" /> Filtros
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-[140px] flex-1 flex-col gap-1 text-xs font-medium text-muted-foreground">
              Guía
              <input placeholder="Parte del nombre" value={filtros.guia} onChange={(e) => cambiarFiltro('guia', e.target.value)}
                className="h-9 rounded-lg border border-input bg-white px-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25" />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              Estado
              <Select value={filtros.estado} size="sm" onChange={(v) => cambiarFiltro('estado', v)}>
                <option value="">Todos</option>
                <option value="falla">Falla</option>
                <option value="alerta">Alerta</option>
                <option value="aprobado">Aprobado</option>
              </Select>
            </label>
            {hayFiltros && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setFiltros({ guia: '', estado: '' })}>
                <X size={14} className="mr-1" aria-hidden="true" /> Limpiar
              </Button>
            )}
          </div>
        </div>

        {error ? (
          <ErrorState title="No se pudieron cargar las inspecciones" message={mensajeError(error)} onRetry={cargar} />
        ) : cargando ? (
          <Cargando texto="Cargando inspecciones…" />
        ) : filtradas.length === 0 ? (
          <EmptyState framed title="Sin inspecciones en este período"
            description={hayFiltros ? 'Ninguna inspección coincide con los filtros.' : 'No hay inspecciones registradas en estas fechas.'} />
        ) : (
          <>
            <div className="hidden sm:block">
              <Table aria-label="Inspecciones del período">
                <Table.Head>
                  <Table.Row>
                    <Table.Th>Fecha</Table.Th>
                    <Table.Th>Guía</Table.Th>
                    <Table.Th>Estado</Table.Th>
                    <Table.Th numeric>Fallas</Table.Th>
                    <Table.Th numeric>Alertas</Table.Th>
                    <Table.Th>Marcas</Table.Th>
                    <Table.Th><span className="sr-only">Abrir</span></Table.Th>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {lista.map((r) => (
                    <Table.Row key={r.id} onClick={() => navigate(rutaInspeccion(r.id))} data-testid="fila-inspeccion"
                      className={r.anulada ? 'text-muted-foreground' : ESTADO_FILA[r.estado_general] || ''}>
                      <Table.Td className={`whitespace-nowrap font-medium tabular-nums ${r.anulada ? 'line-through' : ''}`}>{fechaCorta(r.fecha)}</Table.Td>
                      <Table.Td className={r.anulada ? 'line-through' : ''}>{r.guia_nombre}</Table.Td>
                      <Table.Td><EstadoPill estado={r.estado_general} className={r.anulada ? 'opacity-60' : ''} /></Table.Td>
                      <Table.Td numeric className="font-semibold text-error">{cntF(r, 'falla') || <span className="text-muted-foreground/50">0</span>}</Table.Td>
                      <Table.Td numeric className="font-semibold text-warning-foreground">{cntF(r, 'alerta') || <span className="text-muted-foreground/50">0</span>}</Table.Td>
                      <Table.Td>
                        <Marcas r={r} />
                        {r.anulada && r.anulada_motivo && <div className="mt-0.5 text-xs text-muted-foreground">{r.anulada_motivo}</div>}
                      </Table.Td>
                      <Table.Td className="text-right">
                        <Link to={rutaInspeccion(r.id)} onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:underline">
                          Ver <ChevronRight size={14} aria-hidden="true" />
                        </Link>
                      </Table.Td>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            <div className="space-y-2.5 sm:hidden">
              {lista.map((r) => (
                <Link key={r.id} to={rutaInspeccion(r.id)} data-testid="tarjeta-inspeccion"
                  className={`flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3.5 text-left ${r.anulada ? 'opacity-70' : ESTADO_FILA[r.estado_general] || ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-sm font-bold tabular-nums text-foreground ${r.anulada ? 'line-through' : ''}`}>{fechaCorta(r.fecha)}</span>
                      <EstadoPill estado={r.estado_general} />
                      <Marcas r={r} />
                    </div>
                    <div className={`mt-1 text-sm text-muted-foreground ${r.anulada ? 'line-through' : ''}`}>{r.guia_nombre}</div>
                    {r.anulada && r.anulada_motivo && <div className="mt-1 text-xs text-muted-foreground">Anulada: {r.anulada_motivo}</div>}
                    <Conteos r={r} />
                  </div>
                  <ChevronRight size={18} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                </Link>
              ))}
            </div>

            <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
              <span>Mostrando {lista.length} de {filtradas.length}</span>
              {lista.length < filtradas.length && (
                <Button type="button" variant="outline" onClick={() => setVisibles((v) => v + POR_PAGINA)}>
                  Mostrar {Math.min(POR_PAGINA, filtradas.length - lista.length)} más
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
